import type {
  UpdateEnterpriseRiskMutation,
  UpdateEnterpriseRiskMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

// The tRPC update returns the full row; the V2 GraphQL mutation reported a
// single affected row, so mirror that shape for the flag-gated wrapper.
const mapTrpcResponseToGraphQL = (): UpdateEnterpriseRiskMutation => ({
  updateChildEnterpriseRisk: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

/**
 * tRPC implementation of the update-enterprise-risk mutation (RSP-6498). NOT
 * imported outside this directory — consumers use the flag-gated
 * `useUpdateEnterpriseRisk` wrapper. The `Tier`-discriminated procedure input
 * is built from the flat GraphQL variables (see the insert twin).
 */
export const useUpdateEnterpriseRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.enterpriseRisk.update.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getByTier.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateEnterpriseRisk: async (
      variables: UpdateEnterpriseRiskMutationVariables
    ): Promise<UpdateEnterpriseRiskMutation> => {
      const base = {
        Id: variables.Id,
        Title: variables.Title,
        Description: variables.Description ?? '',
        Treatment: variables.Treatment ?? null,
        CustomAttributeData: variables.CustomAttributeData ?? null,
      };

      if (variables.Tier === 1) {
        await mutation.mutateAsync({ ...base, Tier: 1, ParentId: null });

        return mapTrpcResponseToGraphQL();
      }

      if (!variables.ParentId) {
        throw new Error(
          'A parent enterprise risk is required for tier 2 and tier 3 risks'
        );
      }

      if (variables.Tier === 2) {
        await mutation.mutateAsync({
          ...base,
          Tier: 2,
          ParentId: variables.ParentId,
        });

        return mapTrpcResponseToGraphQL();
      }

      if (variables.Tier === 3) {
        await mutation.mutateAsync({
          ...base,
          Tier: 3,
          ParentId: variables.ParentId,
        });

        return mapTrpcResponseToGraphQL();
      }

      throw new Error(`Unsupported enterprise risk tier: ${variables.Tier}`);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
